import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  TextField,
  Link,
} from "@mui/material";
import { Google, Apple } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Footer from "./blocks/Footer.js";
import Seo from "./blocks/Seo.js";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
} from "firebase/auth";
import { auth } from "../firebase/firebase.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import { getPageRoute } from "src/services";

// Set up providers
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

googleProvider.addScope("profile");
googleProvider.addScope("email");

// Check for Firebase errors
const isFirebaseSignInError = (error) =>
  error.code !== "auth/popup-closed-by-user" &&
  error.code !== "auth/user-cancelled";

const SignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, checkSignIn } = useContext(AuthContext);
  const { showSnackBar, setSnackbarOpen, setSnackbarMessage, setSnackbarSeverity } = useSnackbarContext();

  // Redirect if user is already logged in
  useEffect(() => {
    console.log('SignIn useEffect triggered, user:', user);
    if (user) {
      console.log('User exists, setting timeout for redirect');
      setTimeout(() => {
        console.log('Redirecting to dashboard now');
        navigate(getPageRoute("maestro", "DASHBOARD"));
      }, 2000);
    }
  }, [user, navigate]);

  const signInWithProvider = async (provider, providerName) => {
    try {
      const result = await signInWithPopup(auth, provider);
      await checkSignIn(false, result);
    } catch (err) {
      if (isFirebaseSignInError(err)) {
        let errorMessage = err?.message || "Sign in failed. Please try again.";

        if (err.code === "auth/popup-blocked" || err.code === "auth/cancelled-popup-request") {
          errorMessage = "Your browser may be blocking popups. Please allow popups and try again.";
        }

        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          errorMessage,
          "error"
        );
      }
    }
  };

  const handleSignInWithGoogle = async () => {
    return signInWithProvider(googleProvider, "google");
  };

  const handleSignInWithApple = async () => {
    return signInWithProvider(appleProvider, "apple");
  };

  const handleSendEmail = async () => {
    if (!email) return;

    setIsLoading(true);
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.origin + "/signin?email=" + email,
        handleCodeInApp: true,
      });

      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Link sent, check your inbox.",
        "success"
      );
    } catch (err) {
      console.error(err);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "Failed to send email. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const mediumContainer = {
    margin: "auto",
    maxWidth: "400px",
    minWidth: "286px",
    textAlign: "center",
    p: 6,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Seo
        title="Sign In · Maestro"
        description="Sign in to Maestro."
        noindex
      />

      <Box sx={mediumContainer}>
        <Typography variant="h2" sx={{ mt: 1 }}>
          Sign in to Maestro
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ mb: 2 }}
          color="text.secondary"
        >
          Your AI teaching assistant.
        </Typography>

        {/* Sign-in Buttons */}
        <Stack spacing={2.5}>
          <Button
            variant="outlined"
            size="grown"
            startIcon={<Google />}
            sx={{
              width: "100%",
              fontSize: { xs: "13px", md: "13px" },
            }}
            onClick={handleSignInWithGoogle}
          >
            Continue with Google
          </Button>
          <Button
            variant="outlined"
            size="grown"
            startIcon={<Apple />}
            sx={{
              width: "100%",
              fontSize: { xs: "13px", md: "13px" },
            }}
            onClick={handleSignInWithApple}
          >
            Continue with Apple
          </Button>
        </Stack>

        {/* Divider */}
        <Divider sx={{ my: 2.5 }}>
          <Typography variant="subtitle2">Or with email</Typography>
        </Divider>

        {/* Email Sign-in */}
        <Stack spacing={2.5} sx={{ pb: 2.5 }}>
          <TextField
            type="email"
            data-testid="email-signin"
            value={email}
            label="Email Address"
            variant="standard"
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
          />
          <Button
            variant="contained"
            onClick={handleSendEmail}
            size="grown"
            sx={{ width: "100%" }}
            disabled={!email || isLoading}
          >
            {isLoading ? "Sending..." : "Send sign-in link"}
          </Button>
        </Stack>

        {/* Help Link */}
        <Box sx={{ mt: 1 }}>
          <Link
            href="#"
            variant="body2"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              // TODO: Add help documentation
            }}
          >
            Having issues signing in?
          </Link>
        </Box>
      </Box>

      <Footer />
    </div>
  );
};

export default SignIn;
